/**
 * AULAS — multi-org school model of Custodia (P-10).
 *
 * A classroom groups supervised profiles WITHIN an organization (school):
 * "3rd Grade B", "Computer Lab". Domain rules (mirroring write-path):
 *
 *   - A classroom ALWAYS belongs to an organization (no personal classrooms).
 *   - Assigning a profile to a classroom requires profile and classroom to share the SAME
 *     organization (server re-validates and discards invalid assignments).
 *   - Deleting a classroom DOES NOT delete profiles: server unassigns them
 *     (aula_id = NULL) and classroom is soft-deleted.
 *
 * GOTCHA #15: PowerSync tables are SQLite VIEWS -> NEVER UPSERT.
 * Create = INSERT; edit = UPDATE by id; delete = DELETE.
 */

import { and, asc, eq, isNull } from "drizzle-orm";
import {
  aula,
  type AulaRecord,
  type PowerSyncDrizzleDB,
  supervised_profile,
  type SupervisedProfileRecord,
} from "@custodia/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified } from "../powersync/verified-write";

// ─── Queries (local replica) ────────────────────────────────────────────────

/** Visible active classrooms (org-scoped: arrive only via `org_supervision`). */
export function getAulasQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(aula).where(isNull(aula.deleted_at)).orderBy(asc(aula.name));
}

/** Active classrooms for a specific organization. */
export function getAulasForOrgQuery(db: PowerSyncDrizzleDB, organizationId: string) {
  return db
    .select()
    .from(aula)
    .where(and(eq(aula.organization_id, organizationId), isNull(aula.deleted_at)))
    .orderBy(asc(aula.name));
}

/** Active profiles assigned to a classroom. */
export function getProfilesInAulaQuery(db: PowerSyncDrizzleDB, aulaId: string) {
  return db
    .select()
    .from(supervised_profile)
    .where(and(eq(supervised_profile.aula_id, aulaId), isNull(supervised_profile.deleted_at)));
}

// ─── Mutations (local-first; write-path re-validates authz CC-40) ────────────

export interface CreateAulaInput {
  userId: string;
  /** Owner org — MANDATORY (no personal classrooms). */
  organizationId: string;
  name: string;
  description?: string | null;
}

export async function createAulaMutation(
  db: PowerSyncDrizzleDB,
  input: CreateAulaInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(aula).values({
    id,
    user_id: input.userId,
    organization_id: input.organizationId,
    name: input.name,
    description: input.description ?? null,
    archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export interface UpdateAulaInput {
  name?: string;
  description?: string | null;
  archived?: number;
}

/**
 * `organizationId` is CURRENT `organization_id` of classroom (a classroom ALWAYS
 * belongs to an organization — pass it from `AulaRecord` you already have
 * loaded; column is nullable only due to Drizzle's generated type, never in
 * practice). Serves as `scope` (§ security note in
 * `powersync/verified-write.ts`): a guardian who is a member of multiple school
 * organizations has classrooms of ALL synced simultaneously.
 * `undefined`/`null` omits scope (falls back to by-id only) instead of throwing,
 * in case a caller does not have it handy yet.
 */
export async function updateAulaMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  input: UpdateAulaInput,
  organizationId?: string | null,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) {
    patch.name = input.name;
  }
  if (input.description !== undefined) {
    patch.description = input.description;
  }
  if (input.archived !== undefined) {
    patch.archived = input.archived;
  }
  await patchRowVerified(db, aula, aula.id, id, patch, {
    scope: organizationId ? [{ column: aula.organization_id, value: organizationId }] : [],
  });
}

/**
 * Classroom deletion: local DELETE. Write-path materializes as soft-delete
 * and UNASSIGNS server-side the profiles pointing to it — that is
 * AUTHORITATIVE; unassignment below is purely UI courtesy to avoid
 * waiting for round-trip.
 *
 * Stays OUTSIDE `patchRowsVerified` on purpose, and not just because it is
 * (in spirit) a hard `DELETE` — same reason as rest of module: "no longer
 * present" is the ambiguous window state. The issue runs deeper: the SET
 * of profiles to touch is not known beforehand — must be READ first
 * (`WHERE aula_id = ?`) — and that read lives in the exact same
 * window we want to verify: if a profile newly assigned to this classroom
 * is not yet in the view, it would not even appear in the unassign set,
 * so "verifying" over an already incomplete set gives a false sense of
 * security without closing the gap. Since server unassigns anyway,
 * no need to force it here: worst case, local UI takes one round-trip to
 * reflect empty classroom.
 */
export async function deleteAulaMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db
    .update(supervised_profile)
    .set({ aula_id: null, updated_at: nowISO() })
    .where(eq(supervised_profile.aula_id, id));
  await db.delete(aula).where(eq(aula.id, id));
}

/**
 * Assigns (or unassigns with null) a profile to a classroom. Assignment shares
 * profile with classroom's organization (profile's organization_id = classroom's)
 * — a personal profile becomes visible to rest of school, which
 * is exactly what "putting it in the classroom" means.
 *
 * `currentOrganizationId` is profile's CURRENT `organization_id` (prior to
 * this assignment — `null` if personal); serves as `scope` for same
 * reason as `updateSupervisedProfileMutation`.
 */
export async function assignProfileToAulaMutation(
  db: PowerSyncDrizzleDB,
  profileId: string,
  target: AulaRecord | null,
  currentOrganizationId?: string | null,
): Promise<void> {
  const patch: Record<string, unknown> = {
    aula_id: target?.id ?? null,
  };
  if (target) {
    patch.organization_id = target.organization_id;
  }
  await patchRowVerified(db, supervised_profile, supervised_profile.id, profileId, patch, {
    scope:
      currentOrganizationId === undefined
        ? []
        : [{ column: supervised_profile.organization_id, value: currentOrganizationId }],
  });
}

// ─── Pure helpers (unit-testable; mirror of server rules) ───────────────────

/** Can `profile` be assigned to `target`? Same org (or personal profile being shared). */
export function canAssignProfileToAula(
  profile: Pick<SupervisedProfileRecord, "organization_id">,
  target: Pick<AulaRecord, "organization_id" | "deleted_at">,
): boolean {
  if (target.deleted_at) {
    return false;
  }
  // Personal -> shared with classroom's org; org -> must match.
  return profile.organization_id === null || profile.organization_id === target.organization_id;
}

export interface AulaWithCount {
  aula: AulaRecord;
  profileCount: number;
}

/** Groups classrooms by organization and counts assigned profiles. */
export function groupAulasByOrg(
  aulas: readonly AulaRecord[],
  profiles: readonly Pick<SupervisedProfileRecord, "aula_id" | "deleted_at">[],
): Map<string, AulaWithCount[]> {
  const counts = new Map<string, number>();
  for (const profile of profiles) {
    if (profile.aula_id && !profile.deleted_at) {
      counts.set(profile.aula_id, (counts.get(profile.aula_id) ?? 0) + 1);
    }
  }
  const byOrg = new Map<string, AulaWithCount[]>();
  for (const record of aulas) {
    if (record.deleted_at || !record.organization_id) {
      continue;
    }
    const bucket = byOrg.get(record.organization_id) ?? [];
    bucket.push({ aula: record, profileCount: counts.get(record.id) ?? 0 });
    byOrg.set(record.organization_id, bucket);
  }
  for (const bucket of byOrg.values()) {
    bucket.sort((a, b) => a.aula.name?.localeCompare(b.aula.name ?? "") ?? 0);
  }
  return byOrg;
}

/** Filters profiles by classroom (`null` = no filter; `"none"` = no classroom). */
export function filterProfilesByAula<T extends Pick<SupervisedProfileRecord, "aula_id">>(
  profiles: readonly T[],
  aulaFilter: string | null,
): T[] {
  if (aulaFilter === null) {
    return [...profiles];
  }
  if (aulaFilter === "none") {
    return profiles.filter((p) => !p.aula_id);
  }
  return profiles.filter((p) => p.aula_id === aulaFilter);
}
